
import { supabase } from '@/integrations/supabase/client';
import { ContentType } from '@/services/comments/types';
import { toast } from 'sonner';

/**
 * Fetch vote counts for a specific comment
 */
export const fetchVotesForComment = async (
  commentId: string,
  contentType: ContentType
): Promise<{ netVotes: number; upvotes: number; downvotes: number }> => {
  const table = contentType === 'poll' ? 'poll_comment_votes' : 'card_comment_votes';
  
  try {
    // Count upvotes - use type-safe approach with explicit table names
    let upCount = 0;
    let upError = null;
    
    if (contentType === 'poll') {
      const upResult = await supabase
        .from('poll_comment_votes')
        .select('*', { count: 'exact', head: true })
        .eq('comment_id', commentId)
        .eq('vote_type', 'up');
      
      upCount = upResult.count || 0;
      upError = upResult.error;
    } else {
      const upResult = await supabase
        .from('card_comment_votes')
        .select('*', { count: 'exact', head: true })
        .eq('comment_id', commentId)
        .eq('vote_type', 'up');
      
      upCount = upResult.count || 0;
      upError = upResult.error;
    }
    
    // Count downvotes - use type-safe approach with explicit table names
    let downCount = 0;
    let downError = null;
    
    if (contentType === 'poll') {
      const downResult = await supabase
        .from('poll_comment_votes')
        .select('*', { count: 'exact', head: true })
        .eq('comment_id', commentId)
        .eq('vote_type', 'down');
      
      downCount = downResult.count || 0;
      downError = downResult.error;
    } else {
      const downResult = await supabase
        .from('card_comment_votes')
        .select('*', { count: 'exact', head: true })
        .eq('comment_id', commentId)
        .eq('vote_type', 'down');
      
      downCount = downResult.count || 0;
      downError = downResult.error;
    }
    
    if (upError || downError) {
      console.error('Error fetching vote counts:', { upError, downError });
      return { netVotes: 0, upvotes: 0, downvotes: 0 };
    }
    
    const upvotes = upCount || 0;
    const downvotes = downCount || 0;
    const netVotes = upvotes - downvotes;
    
    return { netVotes, upvotes, downvotes };
  } catch (error) {
    console.error('Error in fetchVotesForComment:', error);
    return { netVotes: 0, upvotes: 0, downvotes: 0 };
  }
};

/**
 * Fetch vote counts for multiple comments and user's votes
 */
export const fetchVoteCounts = async (
  comments: any[],
  contentType: ContentType,
  userId: string | null,
  anonymousId: string | null,
  setters: {
    setVoteCounts: React.Dispatch<React.SetStateAction<Record<string, number>>>;
    setUpvoteCounts: React.Dispatch<React.SetStateAction<Record<string, number>>>;
    setDownvoteCounts: React.Dispatch<React.SetStateAction<Record<string, number>>>;
    setUserVotes: React.Dispatch<React.SetStateAction<Record<string, { up?: boolean; down?: boolean }>>>;
    setMaxVotes: React.Dispatch<React.SetStateAction<number>>;
  }
) => {
  const { setVoteCounts, setUpvoteCounts, setDownvoteCounts, setUserVotes, setMaxVotes } = setters;
  
  if (!comments || comments.length === 0) return;
  
  try {
    const commentIds = comments.map(comment => comment.id);
    const table = contentType === 'poll' ? 'poll_comment_votes' : 'card_comment_votes';
    
    // Fetch all votes for these comments - use type-safe approach with explicit table names
    let votes = null;
    let error = null;
    
    if (contentType === 'poll') {
      const result = await supabase
        .from('poll_comment_votes')
        .select('*')
        .in('comment_id', commentIds);
      
      votes = result.data;
      error = result.error;
    } else {
      const result = await supabase
        .from('card_comment_votes')
        .select('*')
        .in('comment_id', commentIds);
      
      votes = result.data;
      error = result.error;
    }
    
    if (error) {
      console.error('Error fetching votes:', error);
      return;
    }
    
    // Process the votes data
    const voteCounts: Record<string, number> = {};
    const upvoteCounts: Record<string, number> = {};
    const downvoteCounts: Record<string, number> = {};
    const userVotes: Record<string, { up?: boolean; down?: boolean }> = {};
    let maxVotes = 0;
    
    // Initialize counts for all comments
    commentIds.forEach(id => {
      voteCounts[id] = 0;
      upvoteCounts[id] = 0;
      downvoteCounts[id] = 0;
      userVotes[id] = {};
    });
    
    // Process each vote
    if (votes) {
      votes.forEach((vote: any) => {
        const { comment_id, vote_type, user_id, anonymous_id } = vote;
        
        // Update vote counts
        if (vote_type === 'up') {
          upvoteCounts[comment_id] = (upvoteCounts[comment_id] || 0) + 1;
          voteCounts[comment_id] = (voteCounts[comment_id] || 0) + 1;
        } else if (vote_type === 'down') {
          downvoteCounts[comment_id] = (downvoteCounts[comment_id] || 0) + 1;
          voteCounts[comment_id] = (voteCounts[comment_id] || 0) - 1;
        }
        
        // Update max vote count for UI scaling
        const totalVotesForComment = (upvoteCounts[comment_id] || 0) + (downvoteCounts[comment_id] || 0);
        maxVotes = Math.max(maxVotes, totalVotesForComment);
        
        // Check if this vote belongs to the current user
        const isUserVote = 
          (userId && user_id === userId) || 
          (anonymousId && anonymous_id === anonymousId);
        
        if (isUserVote) {
          userVotes[comment_id] = {
            ...userVotes[comment_id],
            [vote_type]: true
          };
        }
      });
    }
    
    // Update state with processed data
    setVoteCounts(voteCounts);
    setUpvoteCounts(upvoteCounts);
    setDownvoteCounts(downvoteCounts);
    setUserVotes(userVotes);
    setMaxVotes(maxVotes);
    
  } catch (error) {
    console.error('Error in fetchVoteCounts:', error);
  }
};

/**
 * Vote for a comment
 */
export const voteForComment = async (
  commentId: string,
  voteType: 'up' | 'down',
  contentType: ContentType,
  userId: string | null,
  anonymousId: string | null,
  state: {
    userVotes: Record<string, { up?: boolean; down?: boolean }>;
    isVoting: Record<string, boolean>;
  },
  setters: {
    setUserVotes: React.Dispatch<React.SetStateAction<Record<string, { up?: boolean; down?: boolean }>>>;
    setVoteCounts: React.Dispatch<React.SetStateAction<Record<string, number>>>;
    setUpvoteCounts: React.Dispatch<React.SetStateAction<Record<string, number>>>;
    setDownvoteCounts: React.Dispatch<React.SetStateAction<Record<string, number>>>;
    setIsVoting: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  }
) => {
  const { userVotes, isVoting } = state;
  const { setUserVotes, setVoteCounts, setUpvoteCounts, setDownvoteCounts, setIsVoting } = setters;
  
  // Prevent multiple votes at once
  if (isVoting[commentId]) {
    return;
  }
  
  if (!userId && !anonymousId) {
    toast.error('You must be logged in to vote');
    return;
  }
  
  try {
    setIsVoting(prev => ({ ...prev, [commentId]: true }));
    
    const table = contentType === 'poll' ? 'poll_comment_votes' : 'card_comment_votes';
    const userHasThisVote = userVotes[commentId]?.[voteType];
    const userHasOppositeVote = voteType === 'up' 
      ? userVotes[commentId]?.down 
      : userVotes[commentId]?.up;
    
    // Optimistic UI update
    setUserVotes(prev => {
      const updatedVotes = { ...prev };
      
      if (userHasThisVote) {
        // Remove this vote type
        updatedVotes[commentId] = {
          ...updatedVotes[commentId],
          [voteType]: false
        };
      } else {
        // Add this vote type and remove opposite if it exists
        updatedVotes[commentId] = {
          ...updatedVotes[commentId],
          [voteType]: true,
          [voteType === 'up' ? 'down' : 'up']: false
        };
      }
      
      return updatedVotes;
    });
    
    // Optimistic update for vote counts
    setVoteCounts(prev => {
      const updatedCounts = { ...prev };
      let change = 0;
      
      if (userHasThisVote) {
        // Removing vote
        change = voteType === 'up' ? -1 : 1;
      } else {
        // Adding vote
        change = voteType === 'up' ? 1 : -1;
        
        // If removing opposite vote, add its effect too
        if (userHasOppositeVote) {
          change += voteType === 'up' ? 1 : -1;
        }
      }
      
      updatedCounts[commentId] = (updatedCounts[commentId] || 0) + change;
      return updatedCounts;
    });
    
    // Optimistic update for upvote/downvote counts
    if (voteType === 'up') {
      setUpvoteCounts(prev => {
        const updated = { ...prev };
        updated[commentId] = (updated[commentId] || 0) + (userHasThisVote ? -1 : 1);
        return updated;
      });
      
      if (userHasOppositeVote) {
        setDownvoteCounts(prev => {
          const updated = { ...prev };
          updated[commentId] = Math.max(0, (updated[commentId] || 0) - 1);
          return updated;
        });
      }
    } else {
      setDownvoteCounts(prev => {
        const updated = { ...prev };
        updated[commentId] = (updated[commentId] || 0) + (userHasThisVote ? -1 : 1);
        return updated;
      });
      
      if (userHasOppositeVote) {
        setUpvoteCounts(prev => {
          const updated = { ...prev };
          updated[commentId] = Math.max(0, (updated[commentId] || 0) - 1);
          return updated;
        });
      }
    }
    
    // Perform the actual API call
    // Call Supabase edge function to handle the vote logic
    const { data, error } = await supabase.functions.invoke('insert_comment_vote', {
      body: JSON.stringify({
        p_comment_id: commentId,
        p_user_id: userId,
        p_anonymous_id: anonymousId,
        p_vote_type: voteType,
        p_table_name: table
      })
    });
    
    if (error) {
      console.error('Error voting:', error);
      toast.error('Failed to submit vote');
      
      // Revert the optimistic updates on error by refetching the current state
      const { netVotes, upvotes, downvotes } = await fetchVotesForComment(commentId, contentType);
      setVoteCounts(prev => ({ ...prev, [commentId]: netVotes }));
      setUpvoteCounts(prev => ({ ...prev, [commentId]: upvotes }));
      setDownvoteCounts(prev => ({ ...prev, [commentId]: downvotes }));
      
      // Reset user vote state by querying actual state
      let userVoteData = null;
      
      if (contentType === 'poll') {
        if (userId) {
          const result = await supabase
            .from('poll_comment_votes')
            .select('vote_type')
            .eq('comment_id', commentId)
            .eq('user_id', userId);
          
          userVoteData = result.data;
        } else if (anonymousId) {
          const result = await supabase
            .from('poll_comment_votes')
            .select('vote_type')
            .eq('comment_id', commentId)
            .eq('anonymous_id', anonymousId);
          
          userVoteData = result.data;
        }
      } else {
        if (userId) {
          const result = await supabase
            .from('card_comment_votes')
            .select('vote_type')
            .eq('comment_id', commentId)
            .eq('user_id', userId);
          
          userVoteData = result.data;
        } else if (anonymousId) {
          const result = await supabase
            .from('card_comment_votes')
            .select('vote_type')
            .eq('comment_id', commentId)
            .eq('anonymous_id', anonymousId);
          
          userVoteData = result.data;
        }
      }
      
      const userVoteState = { up: false, down: false };
      if (userVoteData && userVoteData.length > 0) {
        userVoteData.forEach(vote => {
          if (vote && typeof vote.vote_type === 'string') {
            userVoteState[vote.vote_type as 'up' | 'down'] = true;
          }
        });
      }
      
      setUserVotes(prev => ({ ...prev, [commentId]: userVoteState }));
    } else {
      // Success message based on action
      if (userHasThisVote) {
        toast.success(`${voteType === 'up' ? 'Upvote' : 'Downvote'} removed`);
      } else {
        toast.success(`${voteType === 'up' ? 'Upvoted' : 'Downvoted'} successfully`);
      }
    }
  } catch (e) {
    console.error('Error in voteForComment:', e);
    toast.error('An error occurred while voting');
  } finally {
    setIsVoting(prev => ({ ...prev, [commentId]: false }));
  }
};
